/**
 * Keeps React rendering when something outside it has moved its nodes -
 * `EX-SFX03`, ADR-0108.
 *
 * A browser translator (Chrome's, Edge's) replaces the text nodes it
 * translates, and some extensions wrap or move nodes. React still holds the
 * old nodes, so its next update calls `insertBefore` with a reference node
 * that is no longer a child of the parent, or `removeChild` on a node that has
 * already left, and the DOM throws `NotFoundError`. That is the error a
 * setter's console showed eight times: pressing Save swapped the button's icon
 * beside a translated label, and the page fell to the error screen.
 *
 * The guard answers those two calls the way the page needs instead of
 * throwing: a node already gone is left gone, and a node whose neighbour has
 * moved is put at the end of its parent. At worst a translated word shows
 * twice or an icon sits after its label; the page keeps working. Every repair
 * is counted, so the error screen can tell a translated page from a bug in our
 * code (`lib/crash-report.ts`).
 *
 * Installed once, from the root layout's client side, before the first update
 * can need it. A no-op on the server and in any environment without `Node`.
 */

let repairs = 0;
let installed = false;

/** How many times this page load stepped around a node somebody else moved. */
export function domGuardRepairs(): number {
  return repairs;
}

export function installDomGuard(): void {
  if (installed || typeof Node !== "function" || !Node.prototype) return;
  installed = true;

  const removeChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function guardedRemoveChild<T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      repairs += 1;
      return child;
    }
    return removeChild.call(this, child) as T;
  };

  const insertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function guardedInsertBefore<T extends Node>(
    this: Node,
    node: T,
    reference: Node | null,
  ): T {
    if (reference && reference.parentNode !== this) {
      repairs += 1;
      return insertBefore.call(this, node, null) as T;
    }
    return insertBefore.call(this, node, reference) as T;
  };
}

/** For tests: put the prototype back and the count to zero. */
export function uninstallDomGuardForTests(original: { removeChild: Node["removeChild"]; insertBefore: Node["insertBefore"] }): void {
  Node.prototype.removeChild = original.removeChild;
  Node.prototype.insertBefore = original.insertBefore;
  installed = false;
  repairs = 0;
}
